import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Role } from "@wcp/shared";
import { apiFetch } from "../../lib/api.js";

export interface Connection {
  businessId: string;
  name: string;
  phoneDisplay: string | null;
  wabaId: string | null;
  phoneNumberId: string | null;
  tier: number | null;
  quality: "high" | "medium" | "low" | "unknown";
  metaVerified: boolean;
  displayNameStatus: string | null;
  // Routing slugs (0011) — the Embedded Signup launcher needs them for the exchange.
  wabaSlug: string | null;
  numberSlug: string | null;
  connected: boolean;
}
export interface StoreIntegration {
  id: number;
  slug: string;
  platform: string;
  domain: string;
  apiVersion: string | null;
  status: string;
  hasApiSecret: boolean;
  hasWebhookSecret: boolean;
  webhookPath: string;
}
export interface TeamMember {
  id: number;
  name: string;
  email: string;
  role: Role;
  isActive: boolean;
  lastLoginAt: string | null;
}

export function useConnection(businessId: string) {
  return useQuery<Connection>({
    queryKey: [businessId, "settings", "connection"],
    queryFn: () => apiFetch(`/api/v1/settings/connection?business=${businessId}`),
  });
}

/** Pull live number health (quality/tier/name status/verification) from the Graph API. */
export function useSyncConnection(businessId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<{ synced: number; skipped: number }>("/api/v1/settings/connection/sync", {
        method: "POST",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [businessId, "settings", "connection"] });
      qc.invalidateQueries({ queryKey: [businessId, "dashboard"] });
    },
  });
}

export function useIntegrations(businessId: string) {
  return useQuery<StoreIntegration[]>({
    queryKey: [businessId, "settings", "integrations"],
    queryFn: async () => {
      const r = await apiFetch<{ stores: StoreIntegration[] }>(
        `/api/v1/settings/integrations?business=${businessId}`,
      );
      return r.stores;
    },
  });
}

export function useTeam() {
  return useQuery<TeamMember[]>({
    queryKey: ["team"],
    queryFn: async () => {
      const r = await apiFetch<{ members: TeamMember[] }>("/api/v1/team");
      return r.members;
    },
  });
}

export function useCreateMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; email: string; role: Role; password: string }) =>
      apiFetch("/api/v1/team", { method: "POST", body: JSON.stringify(body) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["team"] }),
  });
}

export function useUpdateMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: number; role?: Role; isActive?: boolean }) =>
      apiFetch(`/api/v1/team/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["team"] }),
  });
}

export interface DemoDataState {
  loaded: boolean;
  available: boolean; // false in production — the toggle is disabled there
}

export function useDemoData() {
  return useQuery<DemoDataState>({
    queryKey: ["settings", "demo-data"],
    queryFn: () => apiFetch("/api/v1/settings/demo-data"),
  });
}

export function useSetDemoData() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (loaded: boolean) =>
      apiFetch<{ loaded: boolean }>("/api/v1/settings/demo-data", {
        method: "PUT",
        body: JSON.stringify({ loaded }),
      }),
    // Loading/clearing demo data changes every screen's data — refetch broadly.
    onSuccess: () => qc.invalidateQueries(),
  });
}

// --- In-panel Embedded Signup (coexistence onboarding) ---

export interface EmbeddedSignupConfig {
  available: boolean;
  appId?: string;
  configId?: string;
  graphVersion?: string;
  wabaSlug?: string;
}

/** Public FB.login config for the launcher (never any secret); available:false when off. */
export function useEmbeddedSignupConfig(wabaSlug: string | null) {
  return useQuery<EmbeddedSignupConfig>({
    queryKey: ["settings", "embedded-signup", wabaSlug],
    queryFn: () =>
      apiFetch(`/api/v1/settings/embedded-signup${wabaSlug ? `?waba=${wabaSlug}` : ""}`),
  });
}

export type OnboardingMode = "coexistence" | "migration";

/** MIGRATION-only register outcome (null/absent for coexistence). Never carries the PIN. */
export interface RegistrationOutcome {
  attempted: boolean;
  success: boolean;
  skippedReason?: string;
  errorCode?: number;
  message?: string;
  retryable?: boolean;
}

export interface ExchangeResponse {
  ok: boolean;
  mode?: OnboardingMode;
  wabaId: string | null;
  phoneNumberId: string | null;
  displayPhoneNumber: string | null;
  verifiedName: string | null;
  registration?: RegistrationOutcome | null;
  note?: string;
}

export interface ExchangeBody {
  mode?: OnboardingMode;
  wabaSlug?: string;
  numberSlug: string;
  code: string;
  wabaId?: string | null;
  phoneNumberId?: string | null;
}

/** Exchange the OAuth code server-side; the token is captured on the box, never returned. */
export function useExchangeEmbeddedSignup(businessId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ExchangeBody) =>
      apiFetch<ExchangeResponse>("/api/v1/settings/embedded-signup/exchange", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: [businessId, "settings", "connection"] }),
  });
}
